/**
 * @file Barre de navigation inferieure mobile pour le panel admin.
 * @module layouts.admin.bottom-nav
 *
 * Barre fixe en bas de l'ecran visible uniquement sur mobile (< lg).
 * Gere les safe areas iOS (home indicator) et Android, en PWA comme en navigateur.
 *
 * Architecture anti-layout-shift :
 * - styled components (pas de sx dynamique = pas de recalcul MUI a chaque render)
 * - React.memo sur le composant racine et sur chaque tab
 * - Une seule icone par tab (pas de swap Iconify entre active/inactive)
 * - Conteneur avec hauteur fixe et layer GPU dedie
 */

import React, {memo, useCallback, useMemo} from 'react';
// @mui
import {styled} from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
// hooks
import {usePermissions} from 'packages/common/hooks/session';
import {usePathname, useRouter} from 'packages/common/routes/hooks';
// components
import Iconify from 'packages/common/components/iconify';
// routes
import {paths} from 'packages/common/routes/paths';
// config
import {SAFE_AREA_X} from '../config-layout';

const BOTTOM_NAV_HEIGHT = 56;

// ---------- Styled components (CSS statique, jamais recalcule) ----------

const NavRoot = styled('nav')(({theme}) => ({
  display: 'block',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  height: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
  // Le fond reste plein bord, les onglets se retirent de l'encoche en paysage
  paddingLeft: SAFE_AREA_X.LEFT,
  paddingRight: SAFE_AREA_X.RIGHT,
  boxSizing: 'border-box',
  zIndex: theme.zIndex.appBar + 1,
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderTop: `1px solid ${theme.palette.divider}`,
  // Layer GPU dedie — isole du reflow du reste de la page
  transform: 'translateZ(0)',
  backfaceVisibility: 'hidden',
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}));

const NavInner = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  height: BOTTOM_NAV_HEIGHT,
});

const TabButton = styled(ButtonBase)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  padding: '6px 4px',
  minWidth: 0,
  position: 'relative',
  transition: 'color 0.2s ease',
});

const ActiveIndicator = styled('span')(({theme}) => ({
  position: 'absolute',
  top: 0,
  left: '25%',
  right: '25%',
  height: 3,
  borderRadius: '0 0 3px 3px',
  backgroundColor: theme.palette.navy[900],
}));

const TabLabel = styled('span')({
  fontSize: '0.625rem',
  lineHeight: 1.2,
  letterSpacing: 0.2,
});

// ---------- Configuration des tabs ----------

const TABS = [
  {
    key: 'home',
    label: 'Dashboard',
    path: paths.admin.root,
    icon: 'mdi:view-dashboard-outline',
    activeIcon: 'mdi:view-dashboard',
    section: null,
    // Racine du panel : correspondance exacte uniquement
    exact: true,
  },
  {
    key: 'companies',
    label: 'Etablissements',
    path: paths.admin.merchants.companies,
    // Sous-arbre companies uniquement : comptes, abonnements ou commissions
    // ne doivent jamais allumer cet onglet
    matchPrefix: paths.admin.merchants.companies,
    icon: 'mdi:store-outline',
    activeIcon: 'mdi:store',
    section: 'companies',
  },
  {
    key: 'analytics',
    label: 'Analytics',
    path: paths.admin.analytics,
    icon: 'mdi:chart-bar',
    activeIcon: 'mdi:chart-bar',
    section: 'analytics',
  },
  {
    key: 'invoices',
    label: 'Factures',
    path: paths.admin.finances.invoices,
    // Pas la racine finances : /finances/commissions est une autre page,
    // l'onglet ne doit pas mentir
    matchPrefix: paths.admin.finances.invoices,
    icon: 'mdi:receipt-text-outline',
    activeIcon: 'mdi:receipt-text',
    section: 'invoices',
  },
];

// ---------- Tab individuel memo ----------

const NavTab = memo(function NavTab({tab, isActive, onNavigate}) {
  const handleClick = useCallback(() => {
    onNavigate(tab.path);
  }, [tab.path, onNavigate]);

  return (
    <TabButton
      onClick={handleClick}
      sx={{color: isActive ? 'navy.900' : 'text.secondary'}}
    >
      {isActive && <ActiveIndicator />}
      <Iconify icon={isActive ? tab.activeIcon : tab.icon} width={24} sx={{width: 24, height: 24, flexShrink: 0}} />
      <TabLabel style={{fontWeight: isActive ? 700 : 500}}>
        {tab.label}
      </TabLabel>
    </TabButton>
  );
});

// ---------- Composant principal ----------

/**
 * Barre de navigation inferieure mobile du panel admin.
 *
 * @return {JSX.Element}
 */
const BottomNav = memo(function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const {hasAdminSection} = usePermissions();

  const visibleTabs = useMemo(
      () => TABS.filter((tab) => tab.section === null || hasAdminSection(tab.section)),
      [hasAdminSection],
  );

  const handleNavigate = useCallback((path) => {
    router.push(path);
  }, [router]);

  return (
    <NavRoot>
      <NavInner>
        {visibleTabs.map((tab) => {
          // Correspondance par segment ('/a/b' matche '/a/b/c', pas '/a/bc') ;
          // exact = pages racines qui ne possèdent aucun sous-arbre
          const base = tab.matchPrefix || tab.path;
          const isActive = pathname === tab.path ||
            (!tab.exact && pathname.startsWith(`${base}/`));

          return (
            <NavTab
              key={tab.key}
              tab={tab}
              isActive={isActive}
              onNavigate={handleNavigate}
            />
          );
        })}
      </NavInner>
    </NavRoot>
  );
});

export default BottomNav;
export {BOTTOM_NAV_HEIGHT};
